import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import {
	useMonthlyScores,
	useUpdateMonthlyScores,
} from '@/api/queries/scoring-queries'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '@/components/ui/accordion'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import { getMonthLabel, MONTH_NUMBERS } from '@/constants/months'
import { cn } from '@/lib/utils'
import type { MonthlyScore, ScoreOwnerType } from '@/types'
import { getApiErrorMessage } from '@/utils/api-error'

const rowKey = (item: Pick<MonthlyScore, 'type' | 'id'>) =>
	`${item.type}:${item.id}`

function isValidScore(value: number) {
	return Number.isInteger(value) && value >= 1 && value <= 100
}

export function MonthlyScoresTab() {
	const { t, i18n } = useTranslation()
	const now = new Date()
	const currentYear = now.getFullYear()
	const yearOptions = [currentYear - 1, currentYear, currentYear + 1]

	const [year, setYear] = useState(currentYear)
	const [month, setMonth] = useState(now.getMonth() + 1)
	// valores digitados, por linha; ausência = mantém a nota vigente
	const [drafts, setDrafts] = useState<Record<string, string>>({})
	// seções abertas do accordion; mantidas ao trocar de mês
	const [openSections, setOpenSections] = useState<string[]>([])

	const { data, isLoading } = useMonthlyScores({ year, month })
	const updateMutation = useUpdateMonthlyScores()

	const items = data?.items ?? []
	const anyEditable = items.some((item) => item.editable)
	const anyLocked = items.some((item) => !item.editable)

	const changeYear = (value: string) => {
		setYear(Number(value))
		setDrafts({})
	}
	const changeMonth = (value: string) => {
		setMonth(Number(value))
		setDrafts({})
	}

	// atividades têm código fixo: o rótulo vem do i18n (fallback: rótulo do backend)
	const displayName = (item: MonthlyScore) =>
		item.type === 'ACTIVITY'
			? t(`administration.scoring.activities.${item.id}`, {
					defaultValue: item.name,
				})
			: item.name

	const formatPeriod = (isoDate: string) => {
		const [y, m] = isoDate.split('-').map(Number)
		return `${getMonthLabel(m, i18n.language)}/${y}`
	}

	const changedItems = items.filter((item) => {
		const draft = drafts[rowKey(item)]
		return (
			draft !== undefined && draft.trim() !== '' && Number(draft) !== item.score
		)
	})

	const handleSave = async () => {
		if (changedItems.length === 0) {
			toast.info(t('administration.scoring.monthly.noChanges'))
			return
		}

		const payload = changedItems.map((item) => ({
			type: item.type,
			id: item.id,
			score: Number(drafts[rowKey(item)]),
		}))
		if (payload.some((p) => !isValidScore(p.score))) {
			toast.error(t('administration.scoring.monthly.invalidScoreError'))
			return
		}

		try {
			await updateMutation.mutateAsync({ year, month, items: payload })
			setDrafts({})
			toast.success(t('administration.scoring.monthly.saveSuccess'))
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		}
	}

	const renderSection = (type: ScoreOwnerType, title: string) => {
		const sectionItems = items.filter((item) => item.type === type)

		const changedCount = changedItems.filter((c) => c.type === type).length
		const withoutScoreCount = sectionItems.filter(
			(i) => i.score === null,
		).length

		return (
			<AccordionItem
				key={type}
				value={type}
				className="rounded-lg border border-kaefer-light-silver bg-white px-4 last:border-b"
			>
				<AccordionTrigger className="items-center gap-3 hover:no-underline">
					<div className="flex flex-wrap items-center gap-2">
						<span className="text-sm font-semibold text-kaefer-dark-grey">
							{title}
						</span>
						<span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-kaefer-medium-grey/15 px-1.5 text-[11px] font-semibold tabular-nums text-kaefer-medium-grey">
							{sectionItems.length}
						</span>
						{withoutScoreCount > 0 && (
							<span className="text-xs font-normal text-muted-foreground">
								{t('administration.scoring.monthly.withoutScoreCount', {
									count: withoutScoreCount,
								})}
							</span>
						)}
						{changedCount > 0 && (
							<Badge className="bg-kaefer-honey-yellow text-kaefer-dark-grey">
								{t('administration.scoring.monthly.changedCount', {
									count: changedCount,
								})}
							</Badge>
						)}
					</div>
				</AccordionTrigger>
				<AccordionContent className="pb-4">
					{sectionItems.length === 0 ? (
						<p className="rounded-lg border border-dashed border-kaefer-light-silver p-4 text-center text-sm text-muted-foreground">
							{t('administration.scoring.monthly.empty')}
						</p>
					) : (
						<div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
							{sectionItems.map((item) => {
								const key = rowKey(item)
								const isChanged = changedItems.some((c) => rowKey(c) === key)

								return (
									<div
										key={key}
										className={cn(
											'flex flex-col gap-3 rounded-lg border bg-white p-3 transition-colors',
											isChanged
												? 'border-kaefer-honey-yellow ring-1 ring-kaefer-honey-yellow'
												: 'border-kaefer-light-silver',
											!item.editable && 'bg-muted/40',
										)}
									>
										<div className="flex items-start justify-between gap-3">
											<span className="min-w-0 text-sm font-medium text-kaefer-dark-grey">
												{displayName(item)}
											</span>
											<Input
												type="number"
												min={1}
												max={100}
												step={1}
												disabled={!item.editable}
												value={drafts[key] ?? item.score?.toString() ?? ''}
												onChange={(e) =>
													setDrafts((prev) => ({
														...prev,
														[key]: e.target.value,
													}))
												}
												placeholder="—"
												aria-label={`${t('administration.scoring.monthly.score')} - ${displayName(item)}`}
												className="h-9 w-20 shrink-0 text-center font-semibold"
											/>
										</div>

										{item.effectiveFrom === null ? (
											<span className="text-xs text-muted-foreground">
												{t('administration.scoring.monthly.noScore')}
											</span>
										) : item.hasOwnEntry ? (
											<Badge variant="secondary" className="w-fit">
												{t('administration.scoring.monthly.sourceOwn')}
											</Badge>
										) : (
											<Badge variant="outline" className="w-fit">
												{t('administration.scoring.monthly.sourceInherited', {
													period: formatPeriod(item.effectiveFrom),
												})}
											</Badge>
										)}
									</div>
								)
							})}
						</div>
					)}
				</AccordionContent>
			</AccordionItem>
		)
	}

	return (
		<div className="flex flex-col gap-4">
			<div className="sticky top-0 z-10 -mx-1 flex flex-col gap-2 bg-white px-1 py-2 sm:flex-row sm:items-center">
				<Select value={String(month)} onValueChange={changeMonth}>
					<SelectTrigger className="w-full sm:w-44">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						{MONTH_NUMBERS.map((value) => (
							<SelectItem key={value} value={String(value)}>
								{getMonthLabel(value, i18n.language)}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				<Select value={String(year)} onValueChange={changeYear}>
					<SelectTrigger className="w-full sm:w-28">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						{yearOptions.map((value) => (
							<SelectItem key={value} value={String(value)}>
								{value}
							</SelectItem>
						))}
					</SelectContent>
				</Select>

				{anyEditable && (
					<Button
						type="button"
						onClick={handleSave}
						disabled={updateMutation.isPending || changedItems.length === 0}
						className="bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90 sm:ml-auto sm:w-auto"
					>
						{updateMutation.isPending ? (
							<>
								<Spinner /> {t('administration.scoring.saving')}
							</>
						) : changedItems.length > 0 ? (
							`${t('administration.scoring.save')} (${changedItems.length})`
						) : (
							t('administration.scoring.save')
						)}
					</Button>
				)}
			</div>

			{!isLoading && anyLocked && (
				<p className="text-xs text-muted-foreground">
					{t('administration.scoring.monthly.readOnlyNotice')}
				</p>
			)}

			{isLoading ? (
				<div className="p-6 text-center text-sm text-muted-foreground">
					{t('administration.scoring.loading')}
				</div>
			) : (
				<Accordion
					type="multiple"
					value={openSections}
					onValueChange={setOpenSections}
					className="space-y-2"
				>
					{renderSection('KIND', t('administration.scoring.monthly.kinds'))}
					{renderSection(
						'CLASSIFICATION',
						t('administration.scoring.monthly.classifications'),
					)}
					{renderSection('QUIZ', t('administration.scoring.monthly.quizzes'))}
					{renderSection(
						'ACTIVITY',
						t('administration.scoring.monthly.activities'),
					)}
				</Accordion>
			)}
		</div>
	)
}
